import { Component, OnInit, ViewChild, AfterViewInit, ElementRef, OnDestroy, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { embedDashboard, type EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { UserComputeRequestsComponent } from '../user-compute-requests/user-compute-requests.component';
import { UserCreditRequestsComponent } from '../user-credit-requests/user-credit-requests.component';
import { SupersetService } from '../../../services/superset.service';
import { environment } from '../../../../environments/environment';
import { WorkspaceContextService } from '../../../services/workspace-context.service';

type TabKey = 'compute-requests' | 'credit-requests';

interface Tab {
  key: TabKey;
  label: string;
}

@Component({
  selector: 'app-compute-credit-home',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    UserComputeRequestsComponent,
    UserCreditRequestsComponent,
  ],
  templateUrl: './compute-credit-home.component.html',
  styleUrl: './compute-credit-home.component.scss',
})
export class ComputeCreditHomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('userCreditRequests') userCreditRequestsComponent!: UserCreditRequestsComponent;
  @ViewChild('userComputeRequests') userComputeRequestsComponent!: UserComputeRequestsComponent;

  @ViewChild('analyticsMount')
  set analyticsMountRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this._analyticsMount = ref;
    if (ref) {
      void this.embedAnalytics();
    }
  }
  private _analyticsMount?: ElementRef<HTMLDivElement>;

  private supersetService = inject(SupersetService);
  protected readonly workspace = inject(WorkspaceContextService);
  private ngZone = inject(NgZone);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private embeddedDashboard: EmbeddedDashboard | null = null;
  private embedToken = 0;

  activeTab: TabKey = 'compute-requests';
  showAnalytics = false;
  analyticsLoading = false;
  analyticsError: string | null = null;

  /** Set true to show Credit Requests approval tab in platform admin */
  readonly creditRequestsEnabled = false;

  readonly computeRoleDashboardId = environment.computeRoleSupersetDashboardId;
  readonly creditRequestDashboardId = environment.supersetDashboardId;

  private readonly allTabs: Tab[] = [
    { key: 'compute-requests', label: 'Compute Role Requests' },
    { key: 'credit-requests', label: 'Credit Requests' },
  ];

  get tabs(): Tab[] {
    return this.creditRequestsEnabled
      ? this.allTabs
      : this.allTabs.filter((tab) => tab.key !== 'credit-requests');
  }

  constructor() {}

  ngOnInit(): void {
    const tabFromUrl = this.route.snapshot.queryParamMap.get('tab');
    if (this.isValidTabKey(tabFromUrl)) {
      this.activeTab = tabFromUrl;
    }
  }

  private isValidTabKey(tab: string | null | undefined): tab is TabKey {
    if (tab === 'credit-requests' && !this.creditRequestsEnabled) {
      return false;
    }
    return tab === 'compute-requests' || tab === 'credit-requests';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.activeTab === 'credit-requests' && this.creditRequestsEnabled) {
        this.loadCreditRequests();
      } else if (this.activeTab === 'compute-requests') {
        this.loadComputeRequests();
      }
    }, 100);
  }

  setActiveTab(tabKey: TabKey): void {
    if (tabKey === 'credit-requests' && !this.creditRequestsEnabled) {
      return;
    }
    if (this.activeTab === tabKey) return;
    this.activeTab = tabKey;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (this.showAnalytics) {
      this.teardownEmbed();
      setTimeout(() => void this.embedAnalytics(), 0);
      return;
    }

    setTimeout(() => {
      if (tabKey === 'credit-requests') {
        this.loadCreditRequests();
      } else if (tabKey === 'compute-requests') {
        this.loadComputeRequests();
      }
    }, 100);
  }

  toggleAnalytics(): void {
    this.showAnalytics = !this.showAnalytics;
    if (!this.showAnalytics) {
      this.teardownEmbed();
      setTimeout(() => {
        if (this.activeTab === 'credit-requests') {
          this.loadCreditRequests();
        } else {
          this.loadComputeRequests();
        }
      }, 100);
      return;
    }
    setTimeout(() => void this.embedAnalytics(), 0);
  }

  private getActiveDashboardId(): string {
    return this.activeTab === 'credit-requests'
      ? this.creditRequestDashboardId
      : this.computeRoleDashboardId;
  }

  private async embedAnalytics(): Promise<void> {
    if (!this.showAnalytics) return;
    const supersetDomain = environment.supersetDashboardUrl?.trim();
    const guestTokenEndpoint = environment.supersetGuestTokenEndpoint?.trim();
    const mountEl = this._analyticsMount?.nativeElement;

    if (!mountEl || !supersetDomain || !guestTokenEndpoint) {
      this.analyticsError = supersetDomain
        ? null
        : 'Superset is not configured (supersetDashboardUrl missing).';
      return;
    }

    const dashboardId = this.getActiveDashboardId();
    const token = ++this.embedToken;
    this.analyticsLoading = true;
    this.analyticsError = null;

    try {
      let guestTokenPromise: Promise<string> | null = this.supersetService.getGuestToken();
      const embedUuid = await this.supersetService.resolveEmbedUuid(dashboardId);

      if (token !== this.embedToken) {
        return;
      }

      const embedded = await embedDashboard({
        id: embedUuid,
        supersetDomain: supersetDomain.replace(/\/+$/, ''),
        mountPoint: mountEl,
        fetchGuestToken: () => {
          const pending = guestTokenPromise ?? this.supersetService.getGuestToken();
          guestTokenPromise = null;
          return pending;
        },
        dashboardUiConfig: {
          hideTitle: true,
          urlParams: { standalone: '1', native_filters_key: '' },
        },
      });

      if (token !== this.embedToken) {
        embedded.unmount();
        return;
      }
      this.embeddedDashboard = embedded;
    } catch {
      if (token === this.embedToken) {
        this.analyticsError = 'Unable to load analytics. Please check guest token middleware.';
      }
    } finally {
      if (token === this.embedToken) {
        // embedDashboard() resolves off an iframe 'load'/postMessage handshake, which can
        // settle outside Angular's zone and leave the UI showing a stale spinner until the
        // next unrelated event. Re-enter the zone explicitly so the view updates immediately.
        this.ngZone.run(() => {
          this.analyticsLoading = false;
        });
      }
    }
  }

  private teardownEmbed(): void {
    this.embedToken++;
    this.embeddedDashboard?.unmount();
    this.embeddedDashboard = null;
    if (this._analyticsMount?.nativeElement) {
      this._analyticsMount.nativeElement.innerHTML = '';
    }
  }

  private loadCreditRequests(): void {
    if (this.userCreditRequestsComponent) {
      this.userCreditRequestsComponent.loadCreditRequests();
    }
  }

  private loadComputeRequests(): void {
    if (this.userComputeRequestsComponent) {
      this.userComputeRequestsComponent.loadComputeRequests();
    }
  }

  onExport(): void {
    if (this.activeTab !== 'compute-requests' || this.showAnalytics) return;
    void this.userComputeRequestsComponent?.onExport();
  }

  ngOnDestroy(): void {
    this.teardownEmbed();
  }
}
